import { OPPORTUNITY_API_URL, AUTH_API_URL } from '../../../config/env';
import axios from 'axios';
import {
  getAccessToken, getCsrfToken,
  setAccessToken, clearAccessToken,
  getRefreshingPromise, setRefreshingPromise, clearRefreshingPromise,
} from '../../../lib/tokenStore';

const opportunityAxios = axios.create({
  withCredentials: true,
  baseURL: OPPORTUNITY_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

opportunityAxios.interceptors.request.use(
  (config) => {
    const csrf = getCsrfToken();
    if (csrf) config.headers['x-csrf-token'] = csrf;

    const token = getAccessToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

opportunityAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const isCsrfMismatch = error.response?.status === 403 && error.response?.data?.message === "CSRF validation failed";

    // Only attempt refresh once per request and only for 401s or 403 CSRF mismatch
    if ((error.response?.status !== 401 && !isCsrfMismatch) || original._retried) {
      return Promise.reject(error);
    }
    original._retried = true;

    if (!getRefreshingPromise()) {
      const refreshPromise = (async () => {
        try {
          const csrf = getCsrfToken();
          const res = await axios.post(
            `${AUTH_API_URL}/refresh`,
            {},
            { withCredentials: true, headers: { ...(csrf ? { 'x-csrf-token': csrf } : {}) } }
          );
          setAccessToken(res.data.data.accessToken);
          if (res.data.data.csrfToken) {
            localStorage.setItem('csrf', res.data.data.csrfToken);
          }
        } catch {
          clearAccessToken();
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('user');
          localStorage.removeItem('name');
          localStorage.removeItem('csrf');
          if (window.location.pathname !== '/login') window.location.href = '/login';
          throw error;
        } finally {
          clearRefreshingPromise();
        }
      })();
      setRefreshingPromise(refreshPromise);
    }
    await getRefreshingPromise();
    original.headers['Authorization'] = `Bearer ${getAccessToken()}`;
    const updatedCsrf = getCsrfToken();
    if (updatedCsrf) {
      original.headers['x-csrf-token'] = updatedCsrf;
    }
    return opportunityAxios(original);
  }
);

// ── Opportunities ─────────────────────────────────────────────────────────────
export const getOpportunities = async ({ type, page = 1, limit = 10, q } = {}) => {
  const response = await opportunityAxios.get('/', { params: { type, page, limit, q: q || undefined } });
  return response.data;
};


export const getOpportunityById = async (id) => {
  const response = await opportunityAxios.get(`/${id}`);
  return response.data;
};

export const getMyPostings = async () => {
  const response = await opportunityAxios.get('/mine');
  return response.data;
};

export const createOpportunity = async (data) => {
  const response = await opportunityAxios.post('/', data);
  return response.data;
};

export const updateOpportunity = async ({ id, data }) => {
  const response = await opportunityAxios.patch(`/${id}`, data);
  return response.data;
};

export const closeOpportunity = async (id) => {
  const response = await opportunityAxios.patch(`/${id}/close`);
  return response.data;
};

export const deleteOpportunity = async (id) => {
  const response = await opportunityAxios.delete(`/${id}`);
  return response.data;
};

export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  // Delete Content-Type so axios sets it automatically to multipart/form-data with correct boundary
  const response = await opportunityAxios.post('/upload', formData, {
    baseURL: OPPORTUNITY_API_URL.replace('/opportunities', ''),
    headers: { 'Content-Type': undefined },
  });
  return response.data;
};

// ── Applications ──────────────────────────────────────────────────────────────
export const applyForOpportunity = async ({ opportunityId, data }) => {
  const response = await opportunityAxios.post(`/${opportunityId}/apply`, data);
  return response.data;
};

export const getMyApplications = async () => {
  const response = await opportunityAxios.get('/my-applications');
  return response.data;
};

export const getApplications = async (opportunityId) => {
  const response = await opportunityAxios.get(`/${opportunityId}/applications`);
  return response.data;
};

export const updateApplicationStatus = async ({ opportunityId, applicationId, status }) => {
  const response = await opportunityAxios.patch(
    `/${opportunityId}/applications/${applicationId}/status`,
    { status }
  );
  return response.data;
};
